import Vue from 'vue'
import Vuex from 'vuex'
import axios from "axios";
import numeral from "numeral";
import "numeral/locales/de";
import * as HA from "@h-a/astrolib";

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    utc: new Date().getTime(),
    placeName: "Unknown",
    location: null,
    culture: "en",
    satellites: [],
    currentlyVisibleSatellites: [],
    selectedSat: null,
    dynamicBlocks: {},
    staticData: null,
    strings: {},
    clockOffset: 0,
    settings: null,
    constellations: {},
    db: null
  },
  mutations: {
    setUtc(state, newUtc) {
      state.utc = newUtc;
    },
    setLocation(state, newLocation) {
      state.location = newLocation;
    },
    setCulture(state, newCulture) {
      state.culture = newCulture;
      // just use the German culture for all exept english and chinese, as most countries seem to use a comma as decimal seperator
      if (newCulture != "en" && newCulture != "zh") numeral.locale("de");
    },
    setSatellites(state, xmlSats) {
      // parse the satellite XML data and create the satellites array
      let parser = new DOMParser();
      let xmlDoc = parser.parseFromString(xmlSats, "text/xml");
      let satElems = xmlDoc.getElementsByTagName("Sat");
      let newSats = [];
      for (let s = 0; s < satElems.length; s++) {
        let satElem = satElems[s];
        let sat = {
          id: satElem.getAttribute("satid"),
          name: satElem.getAttribute("name"),
          orbit: new HA.Sgp4Orbit(satElem.getAttribute("tle")),
          vis: satElem.getAttribute("vis") === "1",
          ham: satElem.getAttribute("ham") === "1"
        };
        if (sat.vis) {
          sat.mag = parseFloat(satElem.getAttribute("mag"));
          sat.maxMag = parseFloat(satElem.getAttribute("maxmag"));
        }
        newSats.push(sat);
      }

      state.satellites = newSats;
    },
    setStrings(state, newStrings) {
      state.strings = newStrings;
    },
    setStaticData(state, newValue) {
      state.staticData = newValue;
    },
    setCurrentlyVisibleSatellites(state, newValue) {
      state.currentlyVisibleSatellites = newValue;
    },
    setSelectedSat(state, newValue) {
      state.selectedSat = newValue;
    },
    setClockOffset(state, newValue) {
      state.clockOffset = newValue;
    },
    setConstellation(state, newValue) {
      if (!state.constellations)
        state.constellations = {};
      state.constellations[newValue.abbreviation] = newValue;
    },
    setSettings(state, newValue) {
      state.settings = newValue;
      localStorage.settings = JSON.stringify(state.settings);
    },
    // addDynamicDataBlock(state, newBlock) {
    //   state.
    // }
  },
  actions: {
    initialise({ commit }) {

      // see if IndexedDB is supported
      // if (!window.indexedDB) {
      //   console.log('This browser doesn\'t support IndexedDB');
      //   return;
      // }
      // else {
      //   let request = window.indexedDB.open('satvis-db', 1);
      //   request.onsuccess = function(event) {
      //     db = event.target.result;  
      //   }
      // }

      // see if settings are already in local storage
      let settings = localStorage.settings;
      if (settings) settings = JSON.parse(settings);
      else {
        // create default settings
        settings = {
          version: 1,
          magLimit: 5,
          elevLimit: 20,
          satSort: 'E',
          showSatLabels: true,
          showConstellationLabels: false,
          showConstellationBoundaries: false,
          showAsterisms: true,
          showStarLabels: false,
          showEcliptic: false,
          showEquator: false,
          showSkyShading: true
        };
      }
      commit("setSettings", settings);

      let now = new Date().getTime();
      let obj = null;

      // see if satellite data is stored in local storage with a recent enough storage date
      let strSats = localStorage.satellites;
      let needSats = true;
      if (strSats) {
        obj = JSON.parse(strSats);
        if (now - obj.t < 12 * 3600000) {
          commit("setSatellites", obj.xml);
          needSats = false;
        }
      }

      if (needSats) {
        // we need to refetch
        this.dispatch("fetchSatelliteData");
      }

      // fetch localized strings if they are not already cached locally
      let cacheKey = "stringsForLiveSkyPage." + this.state.culture;
      let needFetch = true;
      let str = localStorage[cacheKey];
      if (str) {
        obj = JSON.parse(str);
        if (now - obj.t < 6 * 3600000) {
          commit("setStrings", obj.strings);
          needFetch = false;
        }
      }
      if (needFetch)
        this.dispatch("fetchStrings");

      // fetch static data if necessary
      str = localStorage["staticData"];
      if (str) {
        obj = JSON.parse(str);
        commit("setStaticData", obj);
        needFetch = false;
      }
      else
        this.dispatch("fetchStaticData");
        
      // compute clock offset, if the cached copy has expired
      cacheKey = "clockOffset";
      str = localStorage[cacheKey];
      needFetch = true;
      if (str) {
        obj = JSON.parse(str);
        if (now - obj.t < 1800000) {
          commit("setClockOffset", obj.offset);
          needFetch = false;
        }
      }
      else
        this.dispatch("computeClockOffset");

      // fetch dynamic data blocks for today and tomorrow
      this.getters.getDynamicDataBlockForTime(now);
      this.getters.getDynamicDataBlockForTime(now + 86400000);

      // start one second interval timer for updating displays in real-time mode
      setInterval(() => {
        commit("setUtc", new Date().getTime() - this.state.clockOffset);
      }, 1000);

      this.dispatch("checkCacheForExpiredItems");
    },
    checkCacheForExpiredItems() {
      let keysToRemove = [];
      let todayBlockNum = Math.floor((new Date().getTime() - this.state.clockOffset) / 86400000);
      for (let i = 0; i < localStorage.length; i++){
        let cacheKey = localStorage.key(i);
        if (cacheKey.startsWith("pb_")) {
          // for now, simply delete planet blocks which are more than a month in the past
          let blockNum =  parseInt(cacheKey.substr(3));
          if (blockNum + 31 < todayBlockNum)
            keysToRemove.push(cacheKey);
        }
      }

      for (let i = 0; i < keysToRemove.length; i++) {
        localStorage.removeItem(keysToRemove[i]);
        console.log(keysToRemove[i] + ' remove from localStorage');
      }
    },
    fetchSatelliteData({ commit }) {
      axios
        .get("https://heavens-above.com/app/GetSatellites.ashx")
        .then(response => {
          let xml = response.data;
          let cacheObj = {
            t: new Date().getTime(),
            xml: xml
          };
          let strSats = JSON.stringify(cacheObj);
          localStorage.setItem("satellites", strSats);
          commit("setSatellites", xml);
        })
        .catch(error => {
          console.log(error);
        });
    },
    fetchStrings({ commit }) {
      axios
        .get(
          "https://heavens-above.com/StringsForJSON.ashx?cul=" +
          this.state.culture
        )
        .then(response => {
          let key = "stringsForLiveSkyPage." + this.state.culture;
          localStorage.setItem(
            key,
            JSON.stringify({
              t: new Date().getTime(),
              culture: this.state.culture,
              strings: response.data
            })
          );
          commit("setStrings", response.data);
        })
        .catch(error => {
          console.log(error);
        });
    },
    fetchStaticData({ commit }) {
      axios
        .get("/json/SkyChartStaticData3.json")
        .then(response => {
          localStorage.setItem(
            "staticData",
            JSON.stringify(response.data));
          commit("setStaticData", response.data);
        })
        .catch(error => {
          console.log(error);
        });
    },
    /** Load a constellation either from local storage, or from the server */
    loadConstellation({ commit }, id ) {
      let cacheKey = "constellation_" + id;
      let str = localStorage[cacheKey];
      if (str) {
        commit("setConstellation", JSON.parse(str));
      }
      else {
        // not available locally, so fetch from the server
        axios
        .post("http://localhost:65228/graphql", {
          query: "query constellation($id: String) {constellations(id: $id) {abbreviation genitive sizeRank latinName area mythology brightStars {name bayer bayerNum flamsteed ra2000 de2000 mag parallax pmRA pmDE spType hr hd sao hip }}}",
          variables: {id: id},
          operationName: "constellation"
        })
        .then(response => {
          console.log(response.data);
          // cache to local storage
          let constell = response.data.data.constellations[0];
          localStorage.setItem(cacheKey, JSON.stringify(constell));
          commit("setConstellation", constell);
        })
        .catch(error => {
          console.log(error);
        });
      }
    },
    computeClockOffset({ commit }) {
      // get the UTC from the server and calculate the local clock offset
      let startRequestUtc = new Date().getTime();
      axios
        .get("https://heavens-above.com/api2/Time")
        .then(response => {
          let localUtc = new Date().getTime();
          let lag = (localUtc - startRequestUtc) / 2;
          let serverUtc = response.data.utc + lag;
          let offset = localUtc - serverUtc;
          localStorage.setItem(
            "clockOffset",
            JSON.stringify({offset: offset, t: localUtc}));
          commit("setClockOffset", offset);
        })
        .catch(error => {
          console.log(error);
        });
    },
  },
  getters: {
    getDynamicDataBlockForTime: (state) => (t) => {
      // the datablocks are all 24 hours in duration, and aligned on a 24 hour boundary
      let blockNum = Math.floor(t / 86400000).toFixed();
      if (isNaN(blockNum)) return null;

      let blockID = "b" + blockNum;

      // do we already have the block?
      let block = state.dynamicBlocks[blockID];
      if (block && block.loaded)
        return state.dynamicBlocks[blockID];

      if (!block) {
        // we don't have the block, try local storage

        // create function which constructs a Chebyshev trajectory segment from the block coefficients
        let constructTraj = function(fetchedCoeffs, t1, t2) {
          // we have to transpose the fetched coefficients
          let coeffs = [];
          let stride = fetchedCoeffs.length / 3;
          for (let i = 0; i < stride; i += 1) {
            coeffs[i] = fetchedCoeffs[i * 3];
            coeffs[i + stride] = fetchedCoeffs[i * 3 + 1];
            coeffs[i + 2 * stride] = fetchedCoeffs[i * 3 + 2];
          }
          return new HA.ChebyshevTrajectorySegment({
            order: stride - 1,
            coeffs: coeffs,
            centralBody: 399,
            startTime: t1,
            endTime: t2,
            timeStep: 86400000
          });
        }
   
        // function to create block from fetched or cached data
        let blockFromData = function(blockData) {
          let t1 = blockData.t1;
          let t2 = blockData.t2;
          let newBlock = {};
          newBlock.loaded = true;
          newBlock.sunCheby = constructTraj(blockData.sun, t1, t2);
          newBlock.moonCheby = constructTraj(blockData.moon, t1, t2);
          newBlock.planets = [];
          for (let p = 0; p < blockData.planets.length; p++) {
            let planet = {};
            planet.name = blockData.planets[p].name;
            planet.mag = blockData.planets[p].mag;
            planet.cheby = constructTraj(blockData.planets[p].cp, t1, t2);
            newBlock.planets.push(planet);
          }
          return newBlock;
        }

        let cacheKey = "pb_" + blockNum;
        let str = localStorage[cacheKey];
        if (str) {
          block = blockFromData(JSON.parse(str));
          state.dynamicBlocks[blockID] = block;
          return block;
        }
        else {
          // block not available locally, so create an empty block and start a fetch from the server
          state.dynamicBlocks[blockID] = { loaded: false };
          
          axios.get("https://heavens-above.com/GetSkyChartDynamicData.ashx?blockID=" + blockID)
          .then(response => {
            // cache in local storage
            localStorage.setItem(cacheKey, JSON.stringify(response.data));
            let block = blockFromData(response.data);
            state.dynamicBlocks[blockID] = block;
          })
          .catch(error => {
            console.log(error);
          });
          return null;
        }
      }
    },
    getConstellation: (state) => (id) => {
      let constell = state.constellations[id];
      if (constell)
        return constell;
      return null;
    }
  }
})